import { useMemo, useState } from 'react'
import { useStore } from '../store/store'
import { opportunities } from '../store/selectors'
import type { ScoredOpportunity } from '../lib/math'
import { fmtPct } from '../lib/format'
import { Button, Card, EmptyState, PageHead, RangeField, Toggle } from '../components/ui'
import { ExplainPanel, OpportunityCard } from '../components/widgets'

type KindFilter = 'all' | 'stock' | 'prediction' | 'sportsbook'

export function OpportunityScanner() {
  const store = useStore()
  const scanSettings = store.scanSettings
  const ranked = useMemo(() => opportunities(store), [store])

  const [kind, setKind] = useState<KindFilter>('all')
  const [highConfidence, setHighConfidence] = useState(false)
  const [lowCorrelation, setLowCorrelation] = useState(false)
  const [lowDrawdownRisk, setLowDrawdownRisk] = useState(false)
  const [paperOnly, setPaperOnly] = useState(false)
  const [hideSkips, setHideSkips] = useState(true)
  const [explainId, setExplainId] = useState<string | null>(null)

  const filtered = useMemo(() => {
    let list = ranked
    if (kind !== 'all') list = list.filter((o) => o.input.kind === kind)
    if (highConfidence) list = list.filter((o) => o.input.confidence >= 0.6)
    if (lowDrawdownRisk) list = list.filter((o) => o.input.dataQuality >= 0.5 && o.input.liquidity >= 0.5)
    if (paperOnly) list = list.filter((o) => o.action === 'paper')
    if (hideSkips) list = list.filter((o) => !o.noTrade.skip)
    if (lowCorrelation) list = dedupeByGroup(list)
    return list
  }, [ranked, kind, highConfidence, lowDrawdownRisk, paperOnly, hideSkips, lowCorrelation])

  const kinds: KindFilter[] = ['all', 'stock', 'prediction', 'sportsbook']

  return (
    <div className="stack">
      <PageHead title="Opportunity Scanner">
        Every idea with a model probability, ranked by expected value, confidence, liquidity and risk. Positive EV is necessary — not sufficient. Risky edges are flagged.
      </PageHead>

      <Card title="Filters">
        <div className="row wrap" style={{ gap: 6, marginBottom: 10 }}>
          {kinds.map((k) => (
            <button key={k} className={`tab ${kind === k ? 'active' : ''}`} onClick={() => setKind(k)} style={{ textTransform: 'capitalize' }}>
              {k}
            </button>
          ))}
        </div>
        <div className="grid cols-3">
          <div>
            <Toggle label="High confidence only (≥60%)" checked={highConfidence} onChange={setHighConfidence} />
            <Toggle label="Low drawdown risk" checked={lowDrawdownRisk} onChange={setLowDrawdownRisk} />
          </div>
          <div>
            <Toggle label="Low correlation (one per cluster)" checked={lowCorrelation} onChange={setLowCorrelation} />
            <Toggle label="Paper candidates only" checked={paperOnly} onChange={setPaperOnly} />
          </div>
          <div>
            <Toggle label="Hide skips / no-trades" checked={hideSkips} onChange={setHideSkips} />
            <RangeField label="Minimum edge" value={scanSettings.minEdge} onChange={(v) => store.setScanSettings({ minEdge: v })} min={0} max={0.15} step={0.005} display={fmtPct(scanSettings.minEdge, 1)} />
          </div>
        </div>
      </Card>

      {filtered.length === 0 ? (
        <EmptyState>
          Nothing clears the bar with these filters. <strong>That's a valid result</strong> — sometimes there is no trade.
        </EmptyState>
      ) : (
        <div className="grid cols-2">
          {filtered.map((o) => (
            <OpportunityCard
              key={o.input.id}
              opp={o}
              footer={
                <div>
                  <Button size="sm" onClick={() => setExplainId(explainId === o.input.id ? null : o.input.id)}>
                    {explainId === o.input.id ? 'Hide' : 'Explain this edge'}
                  </Button>
                  {explainId === o.input.id && (
                    <div className="card tight" style={{ marginTop: 10 }}>
                      <ExplainPanel explain={o.explain} />
                    </div>
                  )}
                </div>
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

function dedupeByGroup(list: ScoredOpportunity[]): ScoredOpportunity[] {
  const seen = new Set<string>()
  const out: ScoredOpportunity[] = []
  for (const o of list) {
    const g = o.input.correlationGroup
    if (!g) {
      out.push(o)
      continue
    }
    if (seen.has(g)) continue
    seen.add(g)
    out.push(o)
  }
  return out
}
